import React, { useEffect, SyntheticEvent, useRef } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import CloseIcon from '@components/Icons/CloseIcon'
import Transition from '@components/Transition'
import tw from 'twin.macro'
import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock'

type DrawerProps = {
  open: boolean
  handleClose?: (event: SyntheticEvent) => void
  children: React.ReactNode
  header?: React.ReactNode
  duration?: number
  headerHeight?: number
  root?: string
}

const Drawer: React.FC<DrawerProps> = (props) => {
  const { open, handleClose, children, header, duration, headerHeight = 72, root } = props
  const handleClickClose = (event: SyntheticEvent) => {
    event.preventDefault()
    handleClose(event)
  }
  const drawerContentRef = useRef(null)
  useEffect(() => {
    const targetElement = drawerContentRef.current
    if (!targetElement) {
      return
    }
    if (open) {
      disableBodyScroll(targetElement)
    } else {
      enableBodyScroll(targetElement)
    }
    return () => clearAllBodyScrollLocks(targetElement)
  }, [open])

  const result = (
    <Transition open={open} transitionDuration={duration}>
      {({ isTransition, transitionDuration }) => (
        <DrawerContainer
          isTransition={isTransition}
          duration={transitionDuration}
          headerHeight={headerHeight}
        >
          <DrawerHeader>
            <HeaderContent>{header}</HeaderContent>
            <Close onClick={handleClickClose}>
              <CloseIcon />
            </Close>
          </DrawerHeader>
          <DrawerContent ref={drawerContentRef}>{children}</DrawerContent>
        </DrawerContainer>
      )}
    </Transition>
  )

  if (typeof window != 'undefined' && root) {
    return createPortal(result, document.querySelector(root))
  }

  return result
}

export default Drawer

const HeaderContent = styled.div`
  font-size: 20px;
  @media (min-width: 768px) {
    font-size: 16px;
    font-weight: 500;
    ${tw`h-12  px-4 `};
  }
`

const DrawerHeader = styled.div`
  ${tw`px-4 relative flex items-center`};
`

const DrawerContent = styled.div`
  position: absolute;
  bottom: 0;
  -webkit-overflow-scrolling: touch;
  ${tw`overflow-y-scroll w-full`}
`

const DrawerContainer = styled.div<{
  isTransition: boolean
  duration: number
  headerHeight: number
}>`
  background: #fff;
  right: 0;
  transition-duration: ${({ duration }) => `${duration}ms`};
  ${({ isTransition }) => (isTransition ? `bottom: 0` : ` bottom: -100vh`)};
  ${tw`w-full h-full fixed z-modal`}
  ${DrawerHeader} {
    height: ${(props) => `${props.headerHeight}px`};
  }
  ${DrawerContent} {
    top: ${(props) => `${props.headerHeight}px`};
  }
`

const Close = styled.div`
  cursor: pointer;
  margin-left: auto;
  > svg {
    ${tw`w-6 h-6`}
  }
`
